import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { ResultsHubEvents } from '@comparit/core';
import { KundeTokenService, SPARTE_API_BASE_PATH, TokenService } from '@comparit/infrastructure';
import { BehaviorSubject, Subject } from 'rxjs';
import type { ErgebnisInput } from '../interfaces/ergebnis-input.model';

const HUB_METHOD = 'SendBerechnungRequestWithKundeToken';
/** What the hub answers first, before any insurer; the shared page reads these two fields. */
interface BerechnungResponse {
  berechnungId: string;
  anzahlTarife: number;
}

/**
 * The SignalR `/berechnung` client and the store of what a run delivers. Sparte-agnostic: the
 * Sparte travels as an argument, and the results are read structurally as `ErgebnisInput`.
 * `ResultsBaseService` owns when a run starts; this owns how it runs and what it produced.
 */
@Injectable({ providedIn: 'root' })
export class BerechnungHubClient<TErgebnis extends ErgebnisInput = ErgebnisInput> {
  private readonly apiUrl = inject(SPARTE_API_BASE_PATH);
  private readonly vermittlerToken = inject(TokenService);
  private readonly kundeToken = inject(KundeTokenService);
  private connection: HubConnection | null = null;

  readonly allResults$ = new BehaviorSubject<TErgebnis[]>([]);
  readonly all = toSignal(this.allResults$, { initialValue: [] as TErgebnis[] });
  readonly berechnungResponse = signal<BerechnungResponse | null>(null);
  readonly currentNumberOfErgebnisse = computed(() => this.all().length);
  readonly hasError = signal(false);
  readonly noResults = signal(false);
  /** Each batch the hub delivers, as it arrives. */
  readonly ergebnisReceived = new Subject<TErgebnis[]>();

  async start(sparte: string, request: unknown): Promise<void> {
    await this.closeConnection(this.connection);
    this.connection = this.createConnection();

    if (!this.connection) return;
    this.resetData();

    this.registerEventHandlers(this.connection);

    await this.startConnection(this.connection);
    await this.sendRequest(this.connection, request, sparte);
  }

  private createConnection(): HubConnection | null {
    try {
      return new HubConnectionBuilder()
        .withUrl(`${this.apiUrl}/berechnung`, {
          // TODO change later
          withCredentials: false,
        })
        .build();
    } catch (error) {
      console.error('Error building the hub connection:', error);
      return null;
    }
  }

  private registerEventHandlers(connection: HubConnection): void {
    connection.on(ResultsHubEvents.ReceiveVersichererResponse, (data: TErgebnis[]) => {
      this.allResults$.next([...this.allResults$.getValue(), ...data]);
      this.ergebnisReceived.next(data);
    });

    connection.on(ResultsHubEvents.ReceiveBerechnungResponse, (data: BerechnungResponse) => {
      this.berechnungResponse.set(data);
    });
  }

  private async startConnection(connection: HubConnection): Promise<void> {
    if (connection.state !== HubConnectionState.Disconnected) {
      console.warn('Connection already started or in progress:', connection.state);
      return;
    }

    try {
      await connection.start();
    } catch (error) {
      console.error('Error starting connection:', error);
    }
  }

  private async sendRequest(
    connection: HubConnection,
    request: unknown,
    sparte: string
  ): Promise<void> {
    try {
      const res = await connection.invoke<{ error: boolean }>(
        HUB_METHOD,
        sparte,
        request,
        this.vermittlerToken.getToken(),
        this.kundeToken.getToken()
      );
      if (!res.error) {
        if (this.allResults$.getValue().length === 0) {
          this.noResults.set(true);
        }

        const berechnungResponse = this.berechnungResponse();
        if (berechnungResponse) {
          this.berechnungResponse.set({ ...berechnungResponse, anzahlTarife: this.all().length });
        }
        connection.stop();
      } else {
        this.hasError.set(true);
      }
    } catch (error) {
      this.hasError.set(true);
      connection.stop();
      console.error('Error sending request:', error);
    }
  }

  private async closeConnection(connection: HubConnection | null): Promise<void> {
    if (connection?.state === HubConnectionState.Connected) {
      try {
        await connection.stop();
      } catch (error) {
        console.error('Error when stopping the connection:', error);
      }
    }
  }

  private resetData(): void {
    this.noResults.set(false);
    this.hasError.set(false);
    this.allResults$.next([]);
    this.berechnungResponse.set(null);
  }
}
